import { IFeedItem } from "modules/common/models";
import { IActionButtons, IContentOverview } from "modules/common/models";
import { AttachedFile, Image } from "modules/gallery";
import { CroppableImage, DraftAction } from "modules/common/components/authoring/models";
import { ContentState, SortStyle } from "utils/managementUtils";
import { ISimpleUser } from "modules/common/models";

export type AttendanceType = "InPerson" | "NotAttending" | "Online" | "Undecided" | "Waitlist";
export type EventType = "informational" | "mandatory" | "standard";

export interface EventsState {
    allEventsList: EventListingPage;
    drafts: EventListingPage;
    submissions: EventListingPage;
    published: EventListingPage;
    scheduled: EventListingPage;
    documentGallery: DocumentGalleryPage;
    imageGallery: ImageGalleryPage;
    eventsFeed: EventsFeedState;
    pendingDeleting: EventListItem[];
    pendingPublishing?: EventListItem;
    changedSinceSaved: boolean;
    errorMessage: string;
    isDeleting: boolean;
    isFetching: boolean;
    isInitialLoad: boolean;
    isSaving: boolean;
    isUpdatingUrl: boolean;
    isUploading: boolean;
    isSubmitting: boolean;
    shouldFetch: boolean;
    successMessage: SuccessMessage;
    shouldShowDialog: boolean;
    infoMessage: InfoMessage;
    isPublishing: boolean;
}

export interface EventsFeedState {
    eventsFeed: EventFeedItem[];
    canLoadMore: boolean;
    fetching: boolean;
    filters: Partial<EventFeedFilters>;
    filtersApplied: boolean;
    selectedView: string;
    shouldFetch: boolean;
}


export interface Event {
    id: string;
    attachedContent: AttachedFile[];
    author: string;
    authorEmail?: string;
    bannerColor?: string;
    capacity?: Capacity;
    commentingEnabled: boolean;
    createdTime?: Date;
    cutoffTime?: string;
    eventTimes?: EventTimes;
    eventType?: EventType;
    expiryTime?: string;
    fileAttachments: AttachedFile[];
    image?: CroppableImage;
    isPreviouslyPublished?: boolean;
    location?: string;
    locationLink?: string;
    locationLinkLabel?: string;
    notifications: Notifications;
    publishTime?: string;
    status?: string;
    questions: Questions;
    respondingEnabled: boolean;
    tags: Tag[];
    translatedContent: TranslatedContent;
    isSubmission: boolean;
    actionButtons: IActionButtons;
}

export interface EventFeedItem extends IFeedItem {
    attendanceCounts?: any;
    author: {
      avatarColor: string;
      email: string;
      name: string;
    };
    createdTime: string;
    currentUserResponse?: any;
    cutoffTime: string;
    description: string;
    eventEndTime: string;
    eventStartTime: string;
    eventTimes?: any;
    eventType: EventType;
    imageId: string;
    isAllDayEvent: boolean;
    isCapacityAvailable: boolean;
    isCurrentUserAttending: boolean;
    isCurrentUserAttendingInPerson: boolean;
    isCurrentUserAttendingOnline: boolean;
    isCurrentUserNotAttending: boolean;
    isCurrentUserOnWaitlist: boolean;
    isInPersonAvailable: boolean;
    isOnlineAvailable: boolean;
    isResponseEditable: boolean;
    isWaitlistAvailable: boolean;
    location: string;
    locationLink: string;
    locationLinkLabel?: string;
    questions?: any;
    respondingEnabled: boolean;
    showLimitedDisplayNotice: boolean;
    tags: { id: string, mandatory: boolean, name: string }[];
    title: string;
    translatedContent?: TranslatedContent;
    lastReadTime?: string;
}

export interface EventListItem {
    author: string;
    authorFullName: string;
    commentingEnabled: boolean;
    draftId: string;
    eventEndTime: string;
    eventStartTime: string;
    isAllDayEvent: boolean;
    eventType: EventType;
    expiryTime?: Epoch;
    id: string;
    imageUrl?: string;
    isPreviouslyPublished: boolean;
    lastModifiedBy: string;
    lastSubmittedTime: Epoch;
    publishedTime: Epoch;
    rsvpCount: number;
    source: string;
    status: string;
    title: string;
    tags: Tag[];
    updatedTime: Epoch;
    translatedContent?: TranslatedContent;
    alertUnopened: boolean;
}

// return model from adminapi/v1/events/{id}
export interface IEventOverview {
    id: string;
    draftId: string;
    editable: boolean;
    deletable: boolean;
    eventType: string;
    datePublished: Date;
    expiryDate: Date;
    breakingDate: Date;
    featuredDate: Date;
    imageId: string;
    imageUrl: string;
    bannerColor: string;
    tags: Tag[];
    author: string;
    authorEmail: string;
    translatedContent: TranslatedContent;
    attachedContent: AttachedFile[];
    fileAttachments: AttachedFile[];
    location: string;
    locationLink: string;
    eventTimes: EventTimes;
    eventStartTime: Date;
    eventEndTime: Date;
    cutoffTime: Date;
    capacity: Record<AttendanceType, number>;
    questions: Questions;
}

export interface EventView extends IContentOverview {
    attachedContent: AttachedFile[];
    attendanceCounts?: AttendanceCounts;
    author: IEventAuthor;
    bannerColor: string;
    commentEnabled: boolean;
    currentUserResponse?: CurrentUserResponse;
    cutoffTime: string;
    defaultLCID: string;
    eventEndTime: string;
    eventStartTime: string;
    eventTimes: EventTimes;
    eventType: EventType;
    fileAttachments: AttachedFile[];
    id: string;
    imageId: string;
    imageUrl: string;
    isAllDayEvent: boolean;
    isBreaking: boolean;
    isCapacityAvailable: boolean;
    isCurrentUserAttending: boolean;
    isCurrentUserAttendingInPerson: boolean;
    isCurrentUserAttendingOnline: boolean;
    isCurrentUserNotAttending: boolean;
    isCurrentUserOnWaitlist: boolean;
    isExpired: boolean;
    isFeatured: boolean;
    isInPersonAvailable: boolean;
    isOnlineAvailable: boolean;
    isResponseEditable: boolean;
    isRestricted: boolean;
    isWaitlistAvailable: boolean;
    location: string;
    locationLink: string;
    locationLinkLabel: string;
    preferredLCID: string;
    questions: Questions;
    respondingEnabled: boolean;
    sourceId: string;
    tags: Tag[];
    translatedContent: TranslatedContent;
    actionButtons: IActionButtons;
}

export interface Answers {
    [answerNumber: string]: string;
}

export interface AttendanceCount {
    capacity: number;
    current: number;
}

export interface AttendanceCounts {
    InPerson: AttendanceCount;
    NotAttending: AttendanceCount;
    Online: AttendanceCount;
    Waitlist: AttendanceCount;
}

export interface AttendanceFilterValues {
    email: string;
    name: string;
}

export interface AttendanceListingPage {
    id: "" | "all" | "attending" | "notAttending" | "undecided" | "waitlist";
    attendees: Attendee[];
    currentPage: number;
    eventEndTime: string;
    eventStartTime: string;
    eventType: EventType;
    questions: Questions;
    title: string;
    totalAttendees: number;
    totalPages: number;
    isFetching: boolean;
    hasError: boolean;
}

export interface Attendee {
    answers: Answers;
    attendanceType: AttendanceType;
    avatarColor: string;
    email: string;
    id: string;
    name: string;
    rsvpTime: string;
}

export interface Capacity {
    InPerson: number;
    NotAttending: number;
    Online: number;
    Waitlist: number;
}

export interface CurrentUserResponse {
    answers: Answers;
    attendanceType: AttendanceType;
    attendee: IEventAuthor;
}

export interface DocumentGalleryPage {
    currentPage: number;
    documents: AttachedFile[];
    totalDocuments: number;
    totalPages: number;
    isFetching: boolean;
}

export interface Epoch {
    date: Date;
    epoch: Number;
}

export interface EventFeedFilters {
    eventTypes: string[];
    eventStates: ContentState[];
    ignoreSubscriptions: boolean;
    lcidToSearch: string;
    myEventResponses: string[];
    newerThan: string;
    olderThan: string;
    textToSearch: string;
    tags: string[];
}

export interface EventManagementFeedFilters extends EventFeedFilters{
    sortType: SortStyle;
    includeExpired: boolean;
    myEventsOnly: boolean;
}

export const defaultEventsFeedFilter : Partial<EventFeedFilters> = {
  eventTypes: [],
  eventStates: [],
  tags: [],
  textToSearch: ""
};

export const defaultEventFilter = (id: EventListingPageId): EventManagementFeedFilters => ({
    eventTypes: [],
    eventStates: [],
    lcidToSearch: "en-us",
    ignoreSubscriptions: false,
    myEventResponses: [],
    tags: [],
    newerThan: "",
    olderThan: "",
    textToSearch: "",
    sortType: id === "drafts" ? SortStyle.modifiedDesc : SortStyle.startDesc,
    includeExpired: true,
    myEventsOnly: false
});

export type EventListingPageId = "" | "drafts" | "published" | "scheduled" | "all" | "submissions";

export interface EventListingPage {
    id: EventListingPageId;
    currentPage: number;
    filters: EventManagementFeedFilters;
    events: EventListItem[];
    totalEvents: number;
    totalPages: number;
    isFetching: boolean;
}

export interface EventTimes {
    [startTime: string]: string | null;
}

export interface ImageGalleryPage {
    currentPage: number;
    images: Image[];
    totalImages: number;
    totalPages: number;
    isFetching: boolean;
}

export type NotificationRange = "fallback" | "subscribers" | "none";

export interface Notifications {
    emailOnPublish: NotificationRange;
    mobileOnPublish: NotificationRange;
    smsOnPublish: NotificationRange;
    teamsOnPublish: NotificationRange;
    reminders: Reminder[];
}

export interface Question {
    body: string;
    isRequired: boolean;
}

export interface Questions {
    [questionNumber: number]: Question;
}

export interface Reminder {
    channel: ReminderChannel;
    delay: number;
    range: NotificationRange;
}

export type ReminderChannel = "inherit" | "email" | "mobile" | "sms" | "teams" | "none";

export type RSVPType = "initial" | "changeStatus" | "editAnswers";

export interface InfoMessage {
    icon: "" | "cancel";
    message: string;
    severity: "warning" | "info" | "success";
}

export interface SuccessMessage {
    message: string;
    action: DraftAction | "";
}

export interface Tag {
    id: string;
    name: string;
    mandatory?: boolean;
    restricted?: boolean;
}

export interface TranslatedContent {
    [languageCode: string]: TranslatableEventContent;
}

export interface TranslatableEventContent {
    title: string;
    body: string;
    description: string;
}

export interface IEventAuthor extends ISimpleUser {
    avatarColor?: string;
    id?: string;
}

export interface ValidationChecks {
    checks: ValidationCheck[];
}

export interface ValidationCheck {
    key: string;
    description: string;
    isValid: boolean;
    link?: string;
    skip?: boolean;
}
